import { memo } from 'react';
import { placeholderMatches } from '../engine/drawObjects';
import { runsOf } from '../engine/richText';
import { cssOf } from './TextEditor';

/**
 * A paragraph shown as real (read-only) browser text — document mode only.
 *
 * The paragraph being typed in is a contenteditable, i.e. browser text; one
 * painted on the canvas is drawn by a different rasteriser (no hinting, grey
 * smoothing, its own glyph positions) and reads a touch soft beside it, and
 * swapping one for the other on a click shifts the line. Showing every
 * paragraph the way the editor shows it — same box style (`boxStyle`, built by
 * PageView for both), same runs, same yellow [placeholder] chips (the editor
 * paints them with the `pdf-ph` highlight in the same colour) — makes opening
 * one for typing change nothing on screen. The canvas leaves these out.
 */
function StaticText({ obj, scale, boxStyle }) {
    const runs = runsOf(obj);
    const fields = placeholderMatches(obj.text, !!obj.plainBrackets)
        .map((m) => [m.index, m.index + m[0].length]);
    const pieces = [];
    runs.forEach((run, i) => {
        const end = run.start + run.text.length;
        // Cut the run at the field edges, so a [placeholder] gets its chip.
        let at = run.start;
        const cuts = fields
            .filter(([a, b]) => b > run.start && a < end)
            .flatMap(([a, b]) => [Math.max(a, run.start), Math.min(b, end)]);
        for (const cut of [...cuts, end]) {
            if (cut > at) {
                const inField = fields.some(([a, b]) => at >= a && cut <= b);
                pieces.push(
                    <span
                        key={`${i}:${at}`}
                        style={{ ...cssOf(run.style, scale), ...(inField ? { backgroundColor: '#fff3cd' } : null) }}
                    >
                        {obj.text.slice(at, cut)}
                    </span>,
                );
            }
            at = Math.max(at, cut);
        }
    });
    return (
        <div
            aria-hidden
            className="pointer-events-none absolute select-none"
            style={{
                ...cssOf(obj, scale),
                ...boxStyle,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
        >
            {pieces}
        </div>
    );
}

export default memo(StaticText);
